import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { Button } from '@connected/ui/components/button';
import { Popout } from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Pen } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useObservation } from '@/hooks/queries/use-observations';
import { useFormTouched } from '@/hooks/use-form-touched';
import { showConfirm } from '@/stores/confirmer';

import type { ObservationObjectKinds, ObservationParentKinds, ObservationReport, Remark } from '../utils/observation-types';

import { useUpdateObservation } from '../hooks/use-observation-mutations';
import { filterReportsByObjectAndParentKind } from '../utils/observation-utils';
import { ObservationModalContent } from './observation-modal-content';

const DEFAULT_RECOMMENDATION_KIND = 'Same' as const;
const DEFAULT_REMARK_KIND = 'Same' as const;

export type SingleExistingObservationProps<ObjectKind extends ObservationObjectKinds> = {
    item: components['schemas']['ListElectricalProjectObservationQueryResultObservation'];
    electricalProjectVersionId: string;
    objectKind: ObjectKind;
    parentKind: ObservationParentKinds;
};

const remarkDefaultValues: Remark = {
    locationRemark: '',
    remark: '',
    recommendation: '',
    electricalObservationClassificationGroupId: '',
    electricalObservationClassificationCodeId: '',
    codeKind: null,
    electricalProjectFiles: [],
    floorplanElectricalProjectFileId: null,
    floorplanPinCoordinateX: null,
    floorplanPinCoordinateY: null,
    reports: [],
};

export function SingleExistingObservation<ObjectKind extends ObservationObjectKinds>(props: SingleExistingObservationProps<ObjectKind>) {
    const { item, electricalProjectVersionId, objectKind, parentKind } = props;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const { inspectionKindsArray } = useElectricalProject();

    const { data } = useObservation({
        electricalProjectObservationId: item.ElectricalProjectObservationId!,
        electricalProjectVersionId,
    });

    const { mutation: putObservationMutation, disabled } = useUpdateObservation({
        electricalProjectVersionId,
        observationId: item.ElectricalProjectObservationId!,
    });

    const defaultValues = useMemo<Remark>(() => {
        const observation = data?.observation;
        const reports = observation?.Reports || [];

        // Create initial reports array with data from API using unique inspection kinds
        const initialReports = inspectionKindsArray.map((inspectionKind): ObservationReport => {
            const report = reports.find(report => report.InspectionKind === inspectionKind);
            const enabled = report?.Enabled ?? false;

            return {
                codeKind: report?.CodeKind ?? null,
                enabled,
                electricalObservationClassificationCodeId: report?.Code?.ElectricalObservationClassificationCodeId ?? null,
                electricalObservationClassificationGroupId: report?.Group?.ElectricalObservationClassificationGroupId ?? null,
                inspectionKind,
                recommendation: report?.Recommendation ?? null,
                recommendationKind: report?.RecommendationKind ?? DEFAULT_RECOMMENDATION_KIND,
                remark: report?.Remark ?? null,
                remarkKind: report?.RemarkKind ?? DEFAULT_REMARK_KIND,
            };
        });

        return {
            locationRemark: observation?.LocationRemark || remarkDefaultValues.locationRemark,
            remark: observation?.Remark || remarkDefaultValues.remark,
            recommendation: observation?.Recommendation || remarkDefaultValues.recommendation,
            electricalObservationClassificationGroupId: observation?.Group?.ElectricalObservationClassificationGroupId || remarkDefaultValues.electricalObservationClassificationGroupId,
            electricalObservationClassificationCodeId: observation?.Code?.ElectricalObservationClassificationCodeId || remarkDefaultValues.electricalObservationClassificationCodeId,
            codeKind: observation?.CodeKind || remarkDefaultValues.codeKind,
            electricalProjectFiles: observation?.ElectricalProjectFileIds as string[] || [],
            floorplanElectricalProjectFileId: observation?.FloorplanFile?.ElectricalProjectFileId || remarkDefaultValues.floorplanElectricalProjectFileId,
            floorplanPinCoordinateX: observation?.FloorplanFile?.PinCoordinateX || remarkDefaultValues.floorplanPinCoordinateX,
            floorplanPinCoordinateY: observation?.FloorplanFile?.PinCoordinateY || remarkDefaultValues.floorplanPinCoordinateY,
            reports: filterReportsByObjectAndParentKind(initialReports, objectKind, parentKind),
        };
    }, [inspectionKindsArray, data?.observation, objectKind, parentKind]);

    const form = useForm<Remark, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: ({ value }) => {
            putObservationMutation.mutate({
                body: {
                    ElectricalProjectObservationId: item.ElectricalProjectObservationId!,
                    LocationRemark: value.locationRemark,
                    Remark: value.remark,
                    Recommendation: value.recommendation,
                    ElectricalObservationClassificationGroupId: value.electricalObservationClassificationGroupId,
                    ElectricalObservationClassificationCodeId: value.electricalObservationClassificationCodeId,
                    ElectricalProjectFileIds: value.electricalProjectFiles,
                    Order: item.Order || 0,
                    FloorplanElectricalProjectFileId: value.floorplanElectricalProjectFileId,
                    FloorplanPinCoordinateX: value.floorplanPinCoordinateX,
                    FloorplanPinCoordinateY: value.floorplanPinCoordinateY,
                    CodeKind: value.codeKind,
                    Reports: value.reports.map((report) => {
                        // Each report is independent - use enabled directly
                        if (!report.enabled) {
                            return {
                                InspectionKind: report.inspectionKind,
                                Enabled: false,
                                RemarkKind: DEFAULT_REMARK_KIND,
                                Remark: null,
                                RecommendationKind: DEFAULT_RECOMMENDATION_KIND,
                                Recommendation: null,
                                ElectricalObservationClassificationGroupId: null,
                                ElectricalObservationClassificationCodeId: null,
                                CodeKind: null,
                            };
                        }

                        return {
                            InspectionKind: report.inspectionKind,
                            Enabled: report.enabled,
                            RemarkKind: report.remarkKind,
                            Remark: report.remarkKind === 'Custom' ? report.remark : null,
                            RecommendationKind: report.recommendationKind,
                            Recommendation: report.recommendationKind === 'Custom' ? report.recommendation : null,
                            ElectricalObservationClassificationGroupId: report.electricalObservationClassificationGroupId,
                            ElectricalObservationClassificationCodeId: report.electricalObservationClassificationCodeId,
                            CodeKind: report.codeKind,
                        };
                    }),
                },
            });

            form.reset();
        },
    });
    const isFormTouched = useFormTouched(form.store, defaultValues);

    function onSubmit() {
        form.handleSubmit();
        setIsModalOpen(false);
    }

    async function handleChange() {
        if (isFormTouched && isModalOpen) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsModalOpen(false);
                form.reset();
            }
        }
        else {
            setIsModalOpen(!isModalOpen);
            form.reset();
        }
    }

    return (
        <Popout open={isModalOpen} onOpenChange={handleChange}>
            <Button
                variant="ghost"
                onClick={(event) => {
                    event.stopPropagation();
                    setIsModalOpen(true);
                }}
            >
                <Pen className="w-4 h-4" />
            </Button>
            <ObservationModalContent
                type="edit"
                onSubmit={onSubmit}
                onClose={handleChange}
                electricalProjectVersionId={electricalProjectVersionId}
                objectKind={objectKind}
                parentKind={parentKind}
                form={form}
                disabled={disabled}
            />
        </Popout>
    );
}
